import '../styles/HolidaysConfiguration.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCalendar, faEllipsis, faTrash, faPen, faWarning, faXmarkCircle } from '@fortawesome/free-solid-svg-icons';
import { Button, Dropdown, Spinner } from 'react-bootstrap';
import UniInput, { FormControlElement } from '../components/UniInput/UniInput';
import UniPagination from '../components/UniPagination/UniPagination';
import { ChangeEvent, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { HolidayConfigurationDto, get, remove } from '../services/holidaysConfigurationService';
import { useTranslation } from 'react-i18next';
import UniModalHolidayConfiguration from '../components/UniModalHolidayConfiguration/UniModalHolidayConfiguration';
import UniModal from '../components/UniModal/UniModal';

type SearchForm = {
    search?: string
};

const HolidaysConfiguration = () => {
    const [data, setData] = useState<HolidayConfigurationDto[]>();
    const [editItem, setItemEdit] = useState<HolidayConfigurationDto>();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [removeItemId, setRemoveItemId] = useState('');
    const [search, setSearch] = useState<SearchForm>();
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState<boolean>(false);
    const [totalPages, setTotalPages] = useState<number>();
    const [totalItems, setTotalItems] = useState<number>();
    const [take, setTake] = useState<any>(10);
    const [page, setPage] = useState<number>(1);    
    const [reload, setReload] = useState(false);
    const { t, i18n } = useTranslation();

    const dataTable = {
        headers: [
            {
                property: 'country',
                name: t('country'),
                isName: true
            },
            {
                property: 'type',
                name: t('type'),
                isType: true
            },
            {
                property: 'description',
                name: t('description'),
                isName: true
            },
            {
                property: 'year',
                name: t('year'),
                isNumber: true
            },
            {
                property: 'value',
                name: t('dates'),
                isList: true
            },
            {
                property: 'factory',
                name: t('factory'),
                isName: true
            },
            {
                property: 'actions',
                name: t('actions'),
                isAction: true
            },
        ]
    };

    useEffect(() => {
        getData();
    }, [i18n.language, take, page, reload]);

    const onChangeSearch = (e: ChangeEvent<FormControlElement>) => {
        setSearch({ ...search, search: e?.target?.value });
    }

    const getData = () => {
        setLoading(true);
        setErrorMessage('');
        get(i18n.language, take, page, search?.search ?? '')
            .then(response => {
                if (!response.success) {
                    setErrorMessage(response.error ?? '');
                }
                else {                    
                    setData(response.data);
                    setTotalItems(response.totalItems);
                    setTotalPages(response.totalPages);
                }
            })
            .catch(e => console.log(e))
            .finally(() => setLoading(false));
    }

    const displayRemoveModal = (id: string) => {
        setShowRemoveModal(true);
        setRemoveItemId(id);
    }

    const hideRemoveModal = () => {
        setShowRemoveModal(false);
        setRemoveItemId('');
    }

    const removeItem = () => {
        setLoading(true);
        setErrorMessage('');
        remove(removeItemId)
            .then(response => {
                if (response.success) {                    
                    setReload(!reload);
                } else {
                    setErrorMessage(response.error ?? '');                    
                }
            })
            .catch(e => console.log(e))
            .finally(() => setLoading(false));
    }
    
    return (
        <>
            <div className='holidays-configuration'>
                <div className='holidays-configuration-header w-100'>
                    <div className='holidays-configuration-header-i'>
                        <FontAwesomeIcon icon={faCalendar} color='#00FF3C' size='xl' />
                        <p>{t('holidaysConfiguration.menu')}</p>
                    </div>
                    <Button variant='primary' onClick={() => {
                        setShowEditModal(true);
                        setItemEdit({} as HolidayConfigurationDto);
                    }}>
                        <FontAwesomeIcon style={{ paddingRight: '5px' }} icon={faCalendar} color='#00FF3C' size='xl' />
                        {t('new')}
                    </Button>
                </div>
                <div className='holidays-configuration-search w-100'>
                    <div className='w-100'>
                        <UniInput
                            id='search'
                            placeholder={t('holidaysConfiguration.searchFields')}
                            label={t('search')}
                            type='text'
                            onChangeInput={(e) => onChangeSearch(e)}
                        />
                    </div>
                    
                    <FontAwesomeIcon icon={faSearch} title='Buscar' onClick={() => getData()} />
                </div>
                <div className='w-100 table-uni-wrapper'>
                    <table className='table-uni'>
                        <thead>
                            <tr className='table-header'>
                                {
                                    dataTable.headers.map(header => (
                                        <th 
                                            key={header.name}                                            
                                            className={header.isAction ? 'action' : ''}
                                        >
                                            {header.name}
                                        </th>
                                    ))
                                }
                            </tr>
                        </thead>
                        {
                            loading ? (
                                <Skeleton
                                    width={'100%'}
                                    height={400}
                                    duration={1}
                                    highlightColor={'#f5f5f5'}
                                    baseColor={'#ebebeb'} 
                                />
                            ) :
                                (
                                    data?.length === 0 ? 
                                    (
                                        <p className='w-100 text-center p-2'>
                                            {t('searchNotFound')}
                                        </p>
                                    ) : 
                                        <tbody>
                                            {
                                                data?.map((row: any, idx) => (                                                    
                                                    <tr key={idx} className='item-header'>
                                                        {
                                                            dataTable.headers.map(header => {                                                                
                                                                if (header.isName) {
                                                                    return (
                                                                        <td key={header.property + idx}>{row?.[header.property]}</td>
                                                                    )
                                                                }

                                                                if (header.isNumber) {
                                                                    return (
                                                                        <td key={header.property + idx}>{row?.[header.property] ?? '-'}</td>
                                                                    )
                                                                }

                                                                if (header.isList) {
                                                                    return (
                                                                        <td key={header.property + idx}>{row?.[header.property]?.join(', ') ?? '-'}</td>
                                                                    )
                                                                }                                                                

                                                                if (header.isType) {
                                                                    return (
                                                                        <td key={header.property + idx}>{row?.[header.property] ?? '-'}</td>
                                                                    )
                                                                }

                                                                if (header.property === 'actions') {
                                                                    return (
                                                                        <td key={header.property + idx} className='action'>
                                                                            <Dropdown>
                                                                                <Dropdown.Toggle>
                                                                                    <FontAwesomeIcon icon={faEllipsis}/>
                                                                                </Dropdown.Toggle>
                                                                                <Dropdown.Menu style={{padding: 0 }}>
                                                                                    <div className='d-flex flex-column actions gap-1'>
                                                                                        <div
                                                                                            className='action-icon'
                                                                                            style={{border: `1px solid`, fontSize: 11}}
                                                                                            onClick={() => {
                                                                                                setShowEditModal(true);
                                                                                                setItemEdit(row);
                                                                                            }}
                                                                                        >
                                                                                            <FontAwesomeIcon icon={faPen}/>
                                                                                            {t('edit')}
                                                                                        </div>
                                                                                        <div
                                                                                            className='action-icon'
                                                                                            style={{border: `1px solid`, fontSize: 11}}
                                                                                            onClick={() => displayRemoveModal(row.id)}
                                                                                        >
                                                                                            <FontAwesomeIcon icon={faTrash}/>
                                                                                            {t('remove')}
                                                                                        </div>
                                                                                    </div>
                                                                                </Dropdown.Menu>
                                                                            </Dropdown>                                                                            
                                                                        </td>
                                                                    );
                                                                }

                                                                return (
                                                                    <td key={header.property + idx} title={row?.[header.property]}>{row?.[header.property] ? row?.[header.property]?.length > 10 ? row?.[header.property]?.slice(0, 23) + '...' : row?.[header.property] : '-'}</td>
                                                                )
                                                            })
                                                        }
                                                    </tr>
                                                ))
                                            }
                                        </tbody>
                                )
                        }
                    </table>
                </div>
                {
                    <UniPagination totalItems={totalItems} totalPages={totalPages} changePage={(e) => setPage(e)} changeTake={(e) => { setPage(1); setTake(e) }} />
                }
            </div>
            <UniModalHolidayConfiguration 
                item={editItem} 
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setReload(!reload);
                }} 
            />                        
            <UniModal
                icon={faXmarkCircle}
                title={t('errorSave')}
                message=''
                iconColor='#EA2027'
                show={errorMessage !== ''}
                onHide={() => {
                    setErrorMessage('');
                }}
                isFontAwsome
                iconWidth={100}
                iconHeight={110}
            >
                <div className='modal-body-resume'>
                    <p>{errorMessage}</p>
                    <Button onClick={() => setErrorMessage('')}>Ok</Button>
                </div>
            </UniModal>
            <UniModal 
                show={showRemoveModal} 
                icon={faWarning} 
                iconColor={'#EA2027'}
                isFontAwsome
                message={t('removeConfirmation')} 
                title={t('remove')} 
                onHide={() => hideRemoveModal()}
            >
                <div className='buttons-removal-form'>
                    <Button
                        variant='outline'
                        disabled={loading}                            
                        onClick={() => {
                            removeItem();
                            hideRemoveModal();
                        }}
                    >
                        {loading ? <Spinner style={{ width: 20, height: 20 }} /> : t('yes')}
                    </Button>
                    <Button
                        disabled={loading}
                        onClick={() => hideRemoveModal()}
                    >
                        {t('no')}
                    </Button>
                </div>
            </UniModal>
        </>
    );
}

export default HolidaysConfiguration;